import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { TransactionsSummary, TransactionsSummaryRequestBody } from '../../models/transaction.model';
import { TransactionsService } from '../../services/transactions.service';
import { ClearTransactionsState, LoadTransactionVolumeMetrics } from '../actions/transactions.action';
import { buildTransactionsSummaryRequestBody } from '../../utils/transactions-summary-request.util';
import { TqlService } from '@trovata/app/features/tql/services/tql.service';

export interface TransactionsStateModel {
	highVolumeTransactionMode: boolean | null;
	transactionCount: number | null;
	transactionVolumeLoading: boolean;
}

export const HIGH_VOLUME_TRANSACTION_COUNT_THRESHOLD: number = 5_000_000;

const DEFAULT_TRANSACTIONS_STATE: TransactionsStateModel = {
	highVolumeTransactionMode: null,
	transactionCount: null,
	transactionVolumeLoading: false,
};

@State<TransactionsStateModel>({ name: 'transactions', defaults: DEFAULT_TRANSACTIONS_STATE })
@Injectable()
export class TransactionsState {
	private transactionVolumeRequest$: Observable<TransactionsSummary> | null = null;

	constructor(
		private transactionsService: TransactionsService,
		private tqlService: TqlService
	) {}

	@Selector()
	static highVolumeTransactionMode(state: TransactionsStateModel): boolean | null {
		return state.highVolumeTransactionMode;
	}

	@Action(LoadTransactionVolumeMetrics)
	loadTransactionVolumeMetrics(
		context: StateContext<TransactionsStateModel>,
		action: LoadTransactionVolumeMetrics
	): Observable<TransactionsSummary> {
		const state: TransactionsStateModel = context.getState();
		if (state.transactionVolumeLoading && this.transactionVolumeRequest$) {
			return this.transactionVolumeRequest$;
		}

		context.patchState({ transactionVolumeLoading: true });

		const summaryBody: TransactionsSummaryRequestBody = buildTransactionsSummaryRequestBody(
			{
				includeCurrencySummary: false,
			},
			this.tqlService
		);
		const request$: Observable<TransactionsSummary> = this.transactionsService.getTransactionsSummary(summaryBody).pipe(
			tap((summary: TransactionsSummary) => {
				const totalTransactions: number = summary?.totalTransactions ?? 0;
				const isHighVolume: boolean = totalTransactions >= HIGH_VOLUME_TRANSACTION_COUNT_THRESHOLD;
				context.patchState({ highVolumeTransactionMode: isHighVolume, transactionCount: totalTransactions });
			}),
			catchError((error: unknown) => throwError(() => error)),
			finalize(() => {
				context.patchState({ transactionVolumeLoading: false });
				this.transactionVolumeRequest$ = null;
			})
		);

		this.transactionVolumeRequest$ = request$.pipe(shareReplay(1));
		return this.transactionVolumeRequest$;
	}

	@Action(ClearTransactionsState)
	clearTransactionsState(context: StateContext<TransactionsStateModel>): void {
		context.setState({ ...DEFAULT_TRANSACTIONS_STATE });
	}
}
